import { useEffect, useState } from "react";
import api from "@/lib/api";
import { ScrollArea } from "@/components/ui/scroll-area";
import { formatDistanceToNow } from "date-fns";
import { MessageSquare, UserPlus, Tag, CheckCircle2, StickyNote, Eye, AlertOctagon } from "lucide-react";

const ACTION_META = {
  replied: { icon: MessageSquare, label: "replied", color: "text-[#0033CC]" },
  assigned: { icon: UserPlus, label: "assigned conversation", color: "text-blue-600" },
  labels_updated: { icon: Tag, label: "updated labels", color: "text-amber-600" },
  status_changed: { icon: CheckCircle2, label: "changed status", color: "text-green-600" },
  note_added: { icon: StickyNote, label: "added a note", color: "text-amber-700" },
  viewed: { icon: Eye, label: "viewed", color: "text-zinc-500" },
};

export default function ActivityPanel({ threadId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/threads/${threadId}/activity`);
        setItems(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [threadId]);

  return (
    <ScrollArea className="h-full">
      <div className="px-6 py-6 max-w-2xl">
        {loading && <p className="text-sm text-zinc-500">Loading…</p>}
        {!loading && items.length === 0 && (
          <p className="text-sm text-zinc-500 italic" data-testid="activity-empty">No activity yet for this thread.</p>
        )}
        <ol className="relative border-l border-zinc-200 ml-2 space-y-4">
          {items.map((a) => {
            const meta = ACTION_META[a.action] || { icon: AlertOctagon, label: a.action, color: "text-zinc-500" };
            const Icon = meta.icon;
            return (
              <li key={a.id} className="pl-4 relative" data-testid={`activity-${a.id}`}>
                <span className={`absolute -left-[7px] top-1 w-3 h-3 rounded-full bg-white border-2 border-zinc-300 flex items-center justify-center`}>
                  <Icon className={`w-2.5 h-2.5 ${meta.color}`} strokeWidth={2} />
                </span>
                <p className="text-sm">
                  <span className="font-medium">{a.actor_name}</span>{" "}
                  <span className="text-zinc-600">{meta.label}</span>
                  {a.details?.assignee_name && <> <span className="font-medium">{a.details.assignee_name}</span></>}
                  {a.details?.status && <> <span className="font-mono text-xs bg-zinc-100 px-1.5 py-0.5 rounded">{a.details.status}</span></>}
                </p>
                <p className="text-xs text-zinc-500 mt-0.5">{formatDistanceToNow(new Date(a.created_at), { addSuffix: true })}</p>
              </li>
            );
          })}
        </ol>
      </div>
    </ScrollArea>
  );
}
